import { Injectable } from '@angular/core';
import { PresetDateRange, PresetDateRanges, DateRange, PresetDateRangeCategory } from '../../shared/date/date-range';
import { LocalDate } from '@js-joda/core';
import * as jsSha from 'jssha';

/**
 * Service for storing the state of things during the current user session.
 *
 * Includes:
 * - logout for session control.
 * - General purpose load/save/remove for any component to save its own data.
 * - Date range storage, for sharing across components in the application.
 */
@Injectable()
export class SessionStateService {

    /** Storage implementation to use */
    private readonly storage: Storage = sessionStorage;

    private dateRangeState: { [id: string]: DateRangeState } = {};

    /** Construct */
    constructor() {
    }

    /**
     * Prepare local storage for the given username.
     * If the user is different than previously known, clears stored data.
     *
     * @param username user login name. If null, always clears data.
     */
    prepareForUser(username: string) {
        /*
         * Storage key under which to keep track of the current/last user who logged in.
         * Hashed for security.
         * Used to detect if the same user logs back in (after session timeout).
         * If same user, their session state is preserved.
         * Otherwise, session state is reset.
         */
        const LAST_USER_KEY = "last-user";

        let usernameHash = this.getHash(username);
        if (usernameHash == null || usernameHash !== this.storage.getItem(LAST_USER_KEY)) {
            this.storage.clear();
            this.dateRangeState = {};
        }

        if (usernameHash)
            this.storage.setItem(LAST_USER_KEY, usernameHash);
        else
            this.storage.removeItem(LAST_USER_KEY);
    }

    /**
     * Fetch a stored (on new) DateRangeState
     */
    private getDateRangeState(category: PresetDateRangeCategory): DateRangeState {
        let state = this.dateRangeState[category];
        if (!state) {
            state = this.load(category) as DateRangeState;
            if (!state) {
                state = {presetIndex: PresetDateRanges.findIndex(preset => preset.category === category)};
            }

            this.dateRangeState[category] = state;
        }

        return state;
    }

    /**
     * Load any JSON from session state.
     * @param key identifier for the object - perhaps the class that it belongs to?
     * @returns a JSON. Cast to your object type. null if not set.
     */
    load(key: string): any {
        let value = this.storage.getItem(key);
        if (value)
            value = JSON.parse(value);
        return value;
    }

    /**
     * Save any JSON as part of session state.
     * @param key identifier for the object - perhaps the class that it belongs to?
     * @param value a JS object
     */
    save(key: string, value: any) {
        this.storage.setItem(key, JSON.stringify(value));
    }

    /**
     * Remove some state.
     * @param key identifier for the object - perhaps the class that it belongs to?
     */
    remove(key: string) {
        this.storage.removeItem(key);
    }

    /**
     * Set a new PresetDateRange in this state. This clears any custom selection.
     */
    setPresetDateRange(category: PresetDateRangeCategory, preset: PresetDateRange): void {
        let state = this.getDateRangeState(category);
        state.presetIndex = preset.index;
        state.customStart = undefined;
        state.customEnd = undefined;
        this.save(category, state);
    }

    /**
     * Get the current preset selection. (Always valid - returns a default if never selected.)
     * @returns {PresetDateRange}
     */
    getPresetDateRange(category: PresetDateRangeCategory): PresetDateRange {
        return PresetDateRanges[this.getDateRangeState(category).presetIndex];
    }

    /**
     * Set a custom date range selection.
     */
    setCustomDateRange(category: PresetDateRangeCategory, range: DateRange): void {
        let state = this.getDateRangeState(category);
        state.customStart = range.startDate.toEpochDay();
        state.customEnd = range.endDate.toEpochDay();
        this.save(category, state);
    }

    /**
     * Retrieve the custom date range, if set.
     * @returns {DateRange} or undefined
     */
    getCustomDateRange(category: PresetDateRangeCategory): DateRange|undefined {
        let state = this.getDateRangeState(category);
        if (state.customStart && state.customEnd) {
            return new DateRange(
                LocalDate.ofEpochDay(state.customStart),
                LocalDate.ofEpochDay(state.customEnd));
        }
        else {
            return undefined;
        }
    }

    /**
     * Retrieve the current date range - whether custom or a preset.
     * @returns {DateRange}
     */
    getDateRange(category: PresetDateRangeCategory): DateRange {
        if(category === null){
            return PresetDateRanges[this.getDateRangeState(category).presetIndex].getRange();
        }
        return this.getCustomDateRange(category)
            || PresetDateRanges[this.getDateRangeState(category).presetIndex].getRange();
    }

    /**
     * Compute SHA-256 hash for a string.
     */
    private getHash(str: string): string {
        if (str == null)
            return str;

        let shaObj = new jsSha.default("SHA-256", "TEXT");
        shaObj.update(str);
        return shaObj.getHash("HEX");
    }
}

/**
 * Date range selections that are saved between page views and reloads.
 */
export class DateRangeState {
    presetIndex?: number;
    customStart?: number;
    customEnd?: number;
}
